'use client';

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { HStack, VStack, Grid, GridItem } from '@terra/ui-styled-system';
import {
  Text,
  Input,
  IconButton,
  Tooltip,
  Select,
  toasterApi,
  RichMarkdownEditor,
  SingleDatePicker,
} from '@terra/ui-design-system';
import { createListCollection } from '@ark-ui/react';
import {
  Alert01Icon,
  Calendar01Icon,
  File01Icon,
  TestTube01Icon,
  Add01Icon,
  Shield01Icon,
  UserIcon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { terraProxyApiClient } from '../../clients/testerClient';
import type { ReportsReturnDto } from '@terra/shared-contracts-reports';

type MethodologyType = 'white-box' | 'gray-box' | 'black-box';
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const METHODOLOGY_OPTIONS = [
  { value: 'white-box', label: 'White-Box' },
  { value: 'gray-box', label: 'Gray-Box' },
  { value: 'black-box', label: 'Black-Box' },
];
const methodologyCollection = createListCollection({
  items: METHODOLOGY_OPTIONS,
});

export type ReportDetailsTabRef = {
  save: () => Promise<void>;
  isDirty: boolean;
  isSaving: boolean;
};
type ReportDetailsTabProps = {
  report: ReportsReturnDto;
  onUpdate: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onSavingChange?: (isSaving: boolean) => void;
};

export const ReportDetailsTab = forwardRef<
  ReportDetailsTabRef,
  ReportDetailsTabProps
>(function ReportDetailsTab(
  { report, onUpdate, onDirtyChange, onSavingChange },
  ref,
) {
  // Form state
  const [title, setTitle] = useState(report.title ?? '');
  const [contactName, setContactName] = useState('');
  const [reportDate, setReportDate] = useState('');
  const [testPeriodStart, setTestPeriodStart] = useState('');
  const [testPeriodEnd, setTestPeriodEnd] = useState('');
  const [exclusions, setExclusions] = useState<string[]>([]);
  const [limitations, setLimitations] = useState<string[]>([]);
  const [methodologyType, setMethodologyType] =
    useState<MethodologyType>('white-box');
  const [nextStepsSummary, setNextStepsSummary] = useState(
    report.nextStepsSummary ?? '',
  );
  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [newExclusion, setNewExclusion] = useState('');
  const [newLimitation, setNewLimitation] = useState('');
  // Track initial values for dirty state detection
  const [initialValues, setInitialValues] = useState({
    title: '',
    contactName: '',
    reportDate: '',
    testPeriodStart: '',
    testPeriodEnd: '',
    exclusions: [] as string[],
    limitations: [] as string[],
    methodologyType: 'white-box' as MethodologyType,
    nextStepsSummary: '',
  });
  // Calculate dirty state
  const isDirty = useMemo(() => {
    return (
      title !== initialValues.title ||
      contactName !== initialValues.contactName ||
      reportDate !== initialValues.reportDate ||
      testPeriodStart !== initialValues.testPeriodStart ||
      testPeriodEnd !== initialValues.testPeriodEnd ||
      JSON.stringify(exclusions) !== JSON.stringify(initialValues.exclusions) ||
      JSON.stringify(limitations) !==
        JSON.stringify(initialValues.limitations) ||
      methodologyType !== initialValues.methodologyType ||
      nextStepsSummary !== initialValues.nextStepsSummary ||
      newExclusion.trim() !== '' ||
      newLimitation.trim() !== ''
    );
  }, [
    title,
    contactName,
    reportDate,
    testPeriodStart,
    testPeriodEnd,
    exclusions,
    limitations,
    methodologyType,
    nextStepsSummary,
    newExclusion,
    newLimitation,
    initialValues,
  ]);

  // Notify parent of dirty state changes
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);
  // Notify parent of saving state changes
  useEffect(() => {
    onSavingChange?.(isSaving);
  }, [isSaving, onSavingChange]);
  const formatDateForInput = useCallback(
    (date: string | Date | null | undefined): string => {
      if (!date) return '';
      if (typeof date === 'string' && ISO_DATE_PATTERN.test(date)) {
        return date;
      }

      const dateObj = typeof date === 'string' ? new Date(date) : date;

      // Check for Invalid Date - getTime() returns NaN for invalid dates
      if (isNaN(dateObj.getTime())) return '';
      const year = dateObj.getUTCFullYear();
      const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getUTCDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },
    [],
  );
  const parseInputDate = useCallback(
    ({ value }: { value: string }): Date | null => {
      if (ISO_DATE_PATTERN.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        const parsedIsoDate = new Date(Date.UTC(year, month - 1, day));

        if (
          parsedIsoDate.getUTCFullYear() !== year ||
          parsedIsoDate.getUTCMonth() + 1 !== month ||
          parsedIsoDate.getUTCDate() !== day
        ) {
          return null;
        }

        return parsedIsoDate;
      }

      const parsedDate = new Date(value);

      if (Number.isNaN(parsedDate.getTime())) {
        return null;
      }

      return parsedDate;
    },
    [],
  );

  // Initialize form with report data
  useEffect(() => {
    // Type assertion for new fields that may not be in the DTO yet
    const reportData = report as ReportsReturnDto & {
      contactName?: string | null;
      reportDate?: string | Date | null;
      testPeriodStart?: string | Date | null;
      testPeriodEnd?: string | Date | null;
      exclusions?: string[];
      limitations?: string[];
      methodologyType?: MethodologyType;
    };
    // Calculate default dates from findings
    const findingDates = (report.relatedFindings ?? [])
      .map((f) => (f.createdAt ? new Date(f.createdAt) : null))
      .filter((d): d is Date => d !== null && !isNaN(d.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    const earliestFindingDate =
      findingDates.length > 0 ? findingDates[0] : null;
    const latestFindingDate =
      findingDates.length > 0 ? findingDates[findingDates.length - 1] : null;
    const todayDate = formatDateForInput(new Date());
    const newTitle = report.title ?? '';
    const newContactName = reportData.contactName ?? '';
    const newReportDate =
      formatDateForInput(reportData.reportDate) || todayDate;
    const newTestPeriodStart =
      formatDateForInput(reportData.testPeriodStart) ||
      formatDateForInput(earliestFindingDate);
    const newTestPeriodEnd =
      formatDateForInput(reportData.testPeriodEnd) ||
      formatDateForInput(latestFindingDate);
    const newExclusionsList = reportData.exclusions ?? [];
    const newLimitationsList = reportData.limitations ?? [];
    const newMethodologyType = reportData.methodologyType ?? 'white-box';
    const newNextStepsSummary = report.nextStepsSummary ?? '';

    setTitle(newTitle);
    setContactName(newContactName);
    setReportDate(newReportDate);
    setTestPeriodStart(newTestPeriodStart);
    setTestPeriodEnd(newTestPeriodEnd);
    setExclusions(newExclusionsList);
    setLimitations(newLimitationsList);
    setMethodologyType(newMethodologyType);
    setNextStepsSummary(newNextStepsSummary);
    // Set initial values for dirty tracking
    setInitialValues({
      title: newTitle,
      contactName: newContactName,
      reportDate: newReportDate,
      testPeriodStart: newTestPeriodStart,
      testPeriodEnd: newTestPeriodEnd,
      exclusions: newExclusionsList,
      limitations: newLimitationsList,
      methodologyType: newMethodologyType,
      nextStepsSummary: newNextStepsSummary,
    });
  }, [report, formatDateForInput]);
  const handleSave = useCallback(async () => {
    setIsSaving(true);

    try {
      // Include any pending items that weren't explicitly added
      const finalExclusions = newExclusion.trim()
        ? [...exclusions, newExclusion.trim()]
        : exclusions;
      const finalLimitations = newLimitation.trim()
        ? [...limitations, newLimitation.trim()]
        : limitations;
      const parsedReportDate = reportDate
        ? parseInputDate({ value: reportDate })
        : null;
      const parsedTestPeriodStart = testPeriodStart
        ? parseInputDate({ value: testPeriodStart })
        : null;
      const parsedTestPeriodEnd = testPeriodEnd
        ? parseInputDate({ value: testPeriodEnd })
        : null;

      if (
        (reportDate && !parsedReportDate) ||
        (testPeriodStart && !parsedTestPeriodStart) ||
        (testPeriodEnd && !parsedTestPeriodEnd)
      ) {
        toasterApi.error({
          title: 'Invalid date',
          description: 'Please select valid dates before saving the report.',
        });

        return;
      }

      await terraProxyApiClient.updateReport({
        reportId: report.id,
        title: title || undefined,
        nextStepsSummary: nextStepsSummary || null,
        contactName: contactName || null,
        reportDate: parsedReportDate,
        testPeriodStart: parsedTestPeriodStart,
        testPeriodEnd: parsedTestPeriodEnd,
        exclusions: finalExclusions,
        limitations: finalLimitations,
        methodologyType,
      });
      // Update local state only after successful API call
      if (newExclusion.trim()) {
        setExclusions(finalExclusions);
        setNewExclusion('');
      }

      if (newLimitation.trim()) {
        setLimitations(finalLimitations);
        setNewLimitation('');
      }

      // Update initial values to reset dirty state
      setInitialValues({
        title,
        contactName,
        reportDate,
        testPeriodStart,
        testPeriodEnd,
        exclusions: finalExclusions,
        limitations: finalLimitations,
        methodologyType,
        nextStepsSummary,
      });
      toasterApi.success({
        title: 'Report updated',
        description: 'Report details have been saved.',
      });
      onUpdate();
    } catch (error) {
      console.error('Failed to update report:', error);
      toasterApi.error({
        title: 'Update failed',
        description: 'Failed to save report details. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    report.id,
    title,
    nextStepsSummary,
    contactName,
    reportDate,
    testPeriodStart,
    testPeriodEnd,
    exclusions,
    limitations,
    methodologyType,
    newExclusion,
    newLimitation,
    parseInputDate,
    onUpdate,
  ]);

  // Expose methods to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      save: handleSave,
      isDirty,
      isSaving,
    }),
    [handleSave, isDirty, isSaving],
  );
  const handleAddExclusion = useCallback(() => {
    if (newExclusion.trim()) {
      setExclusions((prev) => [...prev, newExclusion.trim()]);
      setNewExclusion('');
    }
  }, [newExclusion]);
  const handleRemoveExclusion = useCallback((index: number) => {
    setExclusions((prev) => prev.filter((_, idx) => idx !== index));
  }, []);
  const handleAddLimitation = useCallback(() => {
    if (newLimitation.trim()) {
      setLimitations((prev) => [...prev, newLimitation.trim()]);
      setNewLimitation('');
    }
  }, [newLimitation]);
  const handleRemoveLimitation = useCallback((index: number) => {
    setLimitations((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  return (
    <VStack gap={3} w="full" alignItems="stretch">
      {/* Report Title & Methodology */}
      <Grid gridTemplateColumns="2fr 1fr" gap={3}>
        <GridItem>
          <VStack gap={1} alignItems="stretch">
            <HStack gap={1.5}>
              <File01Icon size={12} />
              <Text fontSize="xs" fontWeight="medium">
                Report Title
              </Text>
            </HStack>
            <Input
              size="sm"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Security Assessment Report"
            />
          </VStack>
        </GridItem>
        <GridItem>
          <VStack gap={1} alignItems="stretch">
            <HStack gap={1.5}>
              <TestTube01Icon size={12} />
              <Text fontSize="xs" fontWeight="medium">
                Test Type
              </Text>
            </HStack>
            <Select.Root
              size="sm"
              collection={methodologyCollection}
              value={[methodologyType]}
              onValueChange={(details) =>
                setMethodologyType(details.value[0] as MethodologyType)
              }
              positioning={{ strategy: 'fixed' }}
            >
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText />
                </Select.Trigger>
              </Select.Control>
              <Select.Positioner>
                <Select.Content>
                  <Select.ItemGroup>
                    {METHODOLOGY_OPTIONS.map((option) => (
                      <Select.Item key={option.value} item={option.value}>
                        <Select.ItemText>{option.label}</Select.ItemText>
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.ItemGroup>
                </Select.Content>
              </Select.Positioner>
            </Select.Root>
          </VStack>
        </GridItem>
      </Grid>

      {/* Contact Name & Report Date */}
      <Grid gridTemplateColumns="1fr 1fr" gap={3}>
        <GridItem>
          <VStack gap={1} alignItems="stretch">
            <HStack gap={1.5}>
              <UserIcon size={12} />
              <Text fontSize="xs" fontWeight="medium">
                Contact Name
              </Text>
            </HStack>
            <Input
              size="sm"
              value={contactName}
              onChange={(event) => setContactName(event.target.value)}
              placeholder="John Doe"
            />
          </VStack>
        </GridItem>
        <GridItem>
          <VStack gap={1} alignItems="stretch">
            <HStack gap={1.5}>
              <Calendar01Icon size={12} />
              <Text fontSize="xs" fontWeight="medium">
                Report Date
              </Text>
            </HStack>
            <SingleDatePicker
              value={reportDate}
              onChange={setReportDate}
              placeholder="Select date"
              size="sm"
            />
          </VStack>
        </GridItem>
      </Grid>

      {/* Test Period */}
      <Grid gridTemplateColumns="1fr 1fr" gap={3}>
        <GridItem>
          <VStack gap={1} alignItems="stretch">
            <HStack gap={1.5}>
              <Calendar01Icon size={12} />
              <Text fontSize="xs" fontWeight="medium">
                Test Period Start
              </Text>
            </HStack>
            <SingleDatePicker
              value={testPeriodStart}
              onChange={setTestPeriodStart}
              placeholder="Start date"
              size="sm"
            />
          </VStack>
        </GridItem>
        <GridItem>
          <VStack gap={1} alignItems="stretch">
            <HStack gap={1.5}>
              <Calendar01Icon size={12} />
              <Text fontSize="xs" fontWeight="medium">
                Test Period End
              </Text>
            </HStack>
            <SingleDatePicker
              value={testPeriodEnd}
              onChange={setTestPeriodEnd}
              placeholder="End date"
              size="sm"
            />
          </VStack>
        </GridItem>
      </Grid>

      {/* Exclusions */}
      <VStack gap={1.5} alignItems="stretch">
        <HStack gap={1.5}>
          <Shield01Icon size={12} />
          <Text fontSize="xs" fontWeight="medium">
            Exclusions (Out of Scope)
          </Text>
        </HStack>
        <VStack gap={1.5} alignItems="stretch">
          {exclusions.map((exclusion, index) => (
            <HStack key={index} gap={1.5}>
              <Input size="sm" value={exclusion} readOnly flex={1} />
              <Tooltip title="Remove">
                <IconButton
                  size="xs"
                  variant="ghost"
                  aria-label="Remove exclusion"
                  onClick={() => handleRemoveExclusion(index)}
                >
                  <Cancel01Icon size={12} />
                </IconButton>
              </Tooltip>
            </HStack>
          ))}
          <HStack gap={1.5}>
            <Input
              size="sm"
              value={newExclusion}
              onChange={(event) => setNewExclusion(event.target.value)}
              placeholder="Add an exclusion..."
              flex={1}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleAddExclusion();
                }
              }}
            />
            <Tooltip title="Add">
              <IconButton
                size="xs"
                variant="outline"
                aria-label="Add exclusion"
                onClick={handleAddExclusion}
                disabled={!newExclusion.trim()}
              >
                <Add01Icon size={12} />
              </IconButton>
            </Tooltip>
          </HStack>
        </VStack>
      </VStack>

      {/* Limitations */}
      <VStack gap={1.5} alignItems="stretch">
        <HStack gap={1.5}>
          <Alert01Icon size={12} />
          <Text fontSize="xs" fontWeight="medium">
            Limitations
          </Text>
        </HStack>
        <VStack gap={1.5} alignItems="stretch">
          {limitations.map((limitation, index) => (
            <HStack key={index} gap={1.5}>
              <Input size="sm" value={limitation} readOnly flex={1} />
              <Tooltip title="Remove">
                <IconButton
                  size="xs"
                  variant="ghost"
                  aria-label="Remove limitation"
                  onClick={() => handleRemoveLimitation(index)}
                >
                  <Cancel01Icon size={12} />
                </IconButton>
              </Tooltip>
            </HStack>
          ))}
          <HStack gap={1.5}>
            <Input
              size="sm"
              value={newLimitation}
              onChange={(event) => setNewLimitation(event.target.value)}
              placeholder="Add a limitation..."
              flex={1}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleAddLimitation();
                }
              }}
            />
            <Tooltip title="Add">
              <IconButton
                size="xs"
                variant="outline"
                aria-label="Add limitation"
                onClick={handleAddLimitation}
                disabled={!newLimitation.trim()}
              >
                <Add01Icon size={12} />
              </IconButton>
            </Tooltip>
          </HStack>
        </VStack>
      </VStack>

      {/* Strategic Next Steps */}
      <VStack gap={1} alignItems="stretch">
        <HStack gap={1.5}>
          <File01Icon size={12} />
          <Text fontSize="xs" fontWeight="medium">
            Strategic Next Steps
          </Text>
        </HStack>
        <Text fontSize="xs" color="fg.muted">
          Write recommendations for the client. This will appear in the PDF
          report.
        </Text>
        <RichMarkdownEditor
          value={nextStepsSummary ?? ''}
          onChange={(value) => setNextStepsSummary(value)}
          placeholder="## Next Steps

1. Address critical vulnerabilities immediately
2. Implement recommended security controls
3. Schedule follow-up assessment"
          height={350}
          preview="live"
        />
      </VStack>
    </VStack>
  );
});
